import React from 'react';
import { type AggregatedMetrics } from '@prerender/prerender-shared/FE';
import dayjs from 'dayjs';

import { Colors } from '~library/prerenderTheme';
import { numberFormatter } from '~library/numbers';
import { MainChartLegend, ChartCard } from '~components/Charts';
import AverageCrawlTimeMainChart, { MAIN_CHART_ID } from './AverageCrawlTimeMainChart';
import { describeDateRange } from '~components/Filters';
import { useChartItemsVisibility, type ChartConfig } from '~library/chartjs';
import useTrackDashboardV3Event from '../useTrackDashboardV3Event';
import { type WidgetLoadError } from '~features/empty-states';
import ChartLoadError from '../components/ChartLoadError';

export const AVERAGE_CRAWL_TIME_VISIBILITY_CHARTS: ChartConfig[] = [{ id: MAIN_CHART_ID, type: 'dataset' }];

export const TOOLTIP_MESSAGE =
  'The time it takes for crawlers to load your pages, showing times both with and without Prerender. Longer times can lead to delays in indexing new or updated content. Over time, slow crawl speeds can hurt your SEO.';

function ChartPagesCrawled({ rawData }: { rawData: AggregatedMetrics.DeliveryTimeResult }) {
  const track = useTrackDashboardV3Event();
  const ChartItemsVisibility = useChartItemsVisibility(AVERAGE_CRAWL_TIME_VISIBILITY_CHARTS);

  const summary = React.useMemo(() => {
    const totals = rawData.data.reduce<{
      withPrerender: number;
      withoutPrerender: number;
      withPrerenderData: number[];
      withoutPrerenderData: number[];
    }>(
      (acc, value, index) => {
        acc.withPrerender += value.avg;
        acc.withoutPrerender += value.original;
        acc.withPrerenderData[index] = value.avg;
        acc.withoutPrerenderData[index] = value.original;

        return acc;
      },
      {
        withPrerender: 0,
        withoutPrerender: 0,
        withPrerenderData: Array(rawData.intervals.length).fill(0),
        withoutPrerenderData: Array(rawData.intervals.length).fill(0),
      }
    );

    return {
      totals: {
        withPrerender: totals.withPrerender / rawData.data.length,
        withoutPrerender: totals.withoutPrerender / rawData.data.length,
      },
      datasets: [
        { label: 'With Prerender', data: totals.withPrerenderData, backgroundColor: Colors.Mint },
        { label: 'Without Prerender', data: totals.withoutPrerenderData, backgroundColor: Colors.Neutral },
      ],
    };
  }, [rawData.data]);

  const improvementPercentage = numberFormatter.format(summary.totals.withoutPrerender / summary.totals.withPrerender);

  const onLegendItemClicked = (datasetIndex: number) => {
    track('ChartDatasetToggled', {
      chart: 'average crawl time',
      dataset: datasetIndex === 0 ? 'with prerender' : 'without prerender',
      action: ChartItemsVisibility.itemsVisibility[datasetIndex] ? 'hide' : 'show',
    });
    ChartItemsVisibility.toggleVisibility(datasetIndex);
  };

  React.useEffect(() => {
    ChartItemsVisibility.restoreVisibility(summary.datasets.length);
  }, [summary.datasets]);

  return (
    <ChartCard.Content>
      <MainChartLegend>
        <MainChartLegend.LabelBig
          label="With Prerender"
          labelColor={Colors.Mint}
          value={`${numberFormatter.format(summary.totals.withPrerender / 1000)}s`}
          crossedOut={!ChartItemsVisibility.itemsVisibility[0]}
          onClick={() => onLegendItemClicked(0)}
        />

        <MainChartLegend.Label
          label="Without Prerender"
          labelColor={Colors.Neutral}
          secondaryLabel={`/ ${improvementPercentage}x better with Prerender`}
          value={`${numberFormatter.format(summary.totals.withoutPrerender / 1000)}s`}
          crossedOut={!ChartItemsVisibility.itemsVisibility[1]}
          onClick={() => onLegendItemClicked(1)}
        />
      </MainChartLegend>
      <ChartCard.ChartLayout>
        <AverageCrawlTimeMainChart
          labels={rawData.intervals}
          granularity={rawData.granularity}
          datasets={summary.datasets}
        />
      </ChartCard.ChartLayout>
    </ChartCard.Content>
  );
}

export type ChartAverageCrawlTimeProps = {
  rawData?: AggregatedMetrics.DeliveryTimeResult;
  isLoading: boolean;
  error?: WidgetLoadError;
  dateRange: [Date, Date];
  descriptionVisible?: boolean;
};

function ChartAverageCrawlTimeCard({
  rawData,
  isLoading,
  error,
  dateRange,
  descriptionVisible = false,
}: ChartAverageCrawlTimeProps) {
  const activeFilterLabel = React.useMemo(() => describeDateRange(dayjs(dateRange[0]), dayjs(dateRange[1])), [dateRange]);

  return (
    <ChartCard
      isLoading={isLoading}
      errorFallback={error && <ChartLoadError onRetry={error.onRetry} />}
      title={`Average Crawl Time (${activeFilterLabel})`}
      description={TOOLTIP_MESSAGE}
      descriptionType={descriptionVisible ? 'paragraph' : 'tooltip'}
    >
      {rawData && <ChartPagesCrawled rawData={rawData} />}
    </ChartCard>
  );
}

export default ChartAverageCrawlTimeCard;
